import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import AssetsTable from 'sections/futures/UserInfo/AssetsTable'
import { CrossMarginConditionalOrdersTable } from 'sections/futures/UserInfo/ConditionalOrdersTable'
import OrderHistoryTable from 'sections/futures/UserInfo/OrderHistoryTable'
import PositionsTable from 'sections/futures/UserInfo/PositionsTable'
import TradesTable from 'sections/futures/UserInfo/TradesTable'
import TransactionHistoryTable from 'sections/futures/UserInfo/TransactionHistoryTable'
import UserInfoTabs, { FutureTabs, FuturesTab } from 'sections/futures/UserInfo/UserInfoTabs'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectActiveSmartMarginPositionsCount,
	selectAllSmartMarginConditionalOrders,
} from 'state/futures/snxPerpsV2/selectors'
import { PerpsProvider } from 'state/futures/types'
import { useAppSelector } from 'state/hooks'

import { SmartMarginConditionalOrdersTable } from '../../UserInfo/ConditionalOrdersTable'

const UserTabs: React.FC = () => {
	const conditionalOrders = useAppSelector(selectAllSmartMarginConditionalOrders)
	const smartPositionsCount = useAppSelector(selectActiveSmartMarginPositionsCount)
	const futuresType = useAppSelector(selectPerpsProvider)

	const router = useRouter()

	const activeTab = useMemo(() => {
		if (router.query.tab) {
			const tab = router.query.tab as FuturesTab
			if (FutureTabs.includes(tab)) {
				return tab
			}
		}
		return FuturesTab.POSITION
	}, [router])

	const tabs = useMemo(() => {
		const items = [
			{
				tab: FuturesTab.POSITION,
				title: 'Positions',
				component: <PositionsTable />,
				badge: smartPositionsCount,
			},
			{
				tab: FuturesTab.CONDITIONAL_ORDERS,
				title: 'Orders',
				component:
					futuresType === PerpsProvider.SNX_V3 ? (
						<CrossMarginConditionalOrdersTable />
					) : (
						<SmartMarginConditionalOrdersTable />
					),
				badge: conditionalOrders.length,
			},
			{
				tab: FuturesTab.TRANSACTION_HISTORY,
				title: 'Transaction History',
				component: <TransactionHistoryTable />,
			},
			{
				tab: FuturesTab.ORDER_HISTORY,
				title: 'Order History',
				component: <OrderHistoryTable />,
			},
			{
				tab: FuturesTab.TRADES,
				title: 'Trades',
				component: <TradesTable />,
			},
			{
				tab: FuturesTab.ASSETS,
				title: 'Assets',
				component: <AssetsTable />,
			},
		]
		return items
	}, [conditionalOrders.length, smartPositionsCount, futuresType])

	return (
		<Container>
			<UserTabsContainer>
				<TabButtonsContainer>
					<UserInfoTabs activeTab={activeTab} />
				</TabButtonsContainer>
			</UserTabsContainer>
			{tabs.find((t) => t.tab === activeTab)?.component}
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
`

const UserTabsContainer = styled.div`
	overflow: scroll;
	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;

	button {
		font-size: 13px;
	}
`

export default UserTabs
